import React from "react";

import { CheckIcon, CloseIcon, ErrorIcon, SpinnerIcon } from "@/components/icons";
import { cn } from "@/utils/cn";
import type { Notification, NotificationSeverity } from "@/types/notifications";

interface NotificationPanelProps {
  /** Already filtered to unread items by the caller. */
  notifications: Notification[];
  isLoading: boolean;
  unreadCount: number;
  /** Total count across read + unread — used to decide whether the
   *  "View all" footer should render even when the unread list is empty. */
  totalCount: number;
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  onItemClick?: (n: Notification) => void;
  onViewAll?: () => void;
  onClose: () => void;
}

export const severityClasses: Record<NotificationSeverity, string> = {
  info: "text-blue-700 bg-blue-50",
  success: "text-emerald-700 bg-emerald-50",
  warning: "text-amber-800 bg-amber-50",
  error: "text-red-700 bg-red-50",
};

export const severityLabel: Record<NotificationSeverity, string> = {
  info: "Info",
  success: "Done",
  warning: "Heads up",
  error: "Failed",
};

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  notifications,
  isLoading,
  unreadCount,
  totalCount,
  onMarkRead,
  onMarkAllRead,
  onItemClick,
  onViewAll,
  onClose,
}) => {
  const hasReadOnly = notifications.length === 0 && totalCount > 0;
  return (
    <div className="absolute right-0 top-full mt-2 w-96 max-h-[80vh] bg-white border border-gray-200 rounded-lg shadow-xl flex flex-col z-50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
          {unreadCount > 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={onMarkAllRead}
              className="text-xs text-blue-600 hover:text-blue-800"
              title="Mark all as read"
            >
              Mark all read
            </button>
          )}
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            title="Close"
          >
            <CloseIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="overflow-y-auto flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-sm text-gray-500 gap-2">
            <SpinnerIcon className="w-4 h-4" />
            Loading…
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center text-gray-500 text-sm">
            {hasReadOnly ? "You're all caught up." : "No notifications."}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {notifications.map((n) => {
              const sev = (n.severity || "info") as NotificationSeverity;
              const isUnread = n.readAt === null;
              return (
                <li
                  key={n.id}
                  className={cn(
                    "px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors",
                    isUnread && "bg-blue-50/30",
                  )}
                  onClick={() => {
                    if (isUnread) onMarkRead(n.id);
                    onItemClick?.(n);
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={cn(
                        "shrink-0 mt-0.5 w-7 h-7 rounded-md flex items-center justify-center",
                        severityClasses[sev],
                      )}
                      title={severityLabel[sev]}
                    >
                      {sev === "error" ? (
                        <ErrorIcon className="w-4 h-4" />
                      ) : sev === "success" ? (
                        <CheckIcon className="w-4 h-4" />
                      ) : (
                        <span className="w-2 h-2 rounded-full bg-current" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <p
                          className={cn(
                            "text-sm truncate",
                            isUnread ? "font-semibold text-gray-900" : "font-medium text-gray-700",
                          )}
                        >
                          {n.title}
                        </p>
                        <span className="text-[10px] text-gray-500 shrink-0">
                          {formatRelativeTime(n.createdAt)}
                        </span>
                      </div>
                      {n.body && (
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                          {n.body}
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Footer */}
      {onViewAll && totalCount > 0 && (
        <div className="border-t border-gray-100 px-4 py-2 shrink-0">
          <button
            onClick={onViewAll}
            className="w-full text-center text-xs font-medium text-blue-600 hover:text-blue-800 py-1"
          >
            View all ({totalCount})
          </button>
        </div>
      )}
    </div>
  );
};

export function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return date.toLocaleDateString();
}
